import React, { useState, useEffect } from 'react';
import { auditApi } from '../services/api';
import { Card, Select, Spinner, Badge, Tabs } from '../components/UI';
import { ClockCounterClockwise, User, FileText, SignIn, SignOut } from '@phosphor-icons/react';

const ACTION_COLORS = {
  created: 'success', updated: 'primary', deleted: 'danger', status_changed: 'warning',
  approved: 'success', rejected: 'danger', version_added: 'primary',
  LOGIN: 'success', LOGOUT: 'default'
};

export default function AuditLogPage() {
  const [activeTab, setActiveTab] = useState('activity');
  const [logs, setLogs] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('');
  const [filterAction, setFilterAction] = useState('');

  useEffect(() => {
    Promise.all([
      auditApi.logs({ limit: 200 }),
      auditApi.loginHistory(100)
    ]).then(([a, l]) => {
      setLogs(a.data);
      setLoginLogs(l.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = { limit: 200 };
    if (filterEntity) params.entity_type = filterEntity;
    if (filterAction) params.action = filterAction;
    auditApi.logs(params).then(r => setLogs(r.data));
  }, [filterEntity, filterAction]);

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="audit-log-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0A0A0A]">Audit & Activity Log</h1>
        <p className="text-sm text-[#666]">Track user actions, system changes & login history</p>
      </div>

      <Tabs
        tabs={[{ id: 'activity', label: `Activity Log (${logs.length})` }, { id: 'logins', label: `Login History (${loginLogs.length})` }]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === 'activity' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)} data-testid="filter-entity">
              <option value="">All Entities</option>
              <option value="document">Documents</option>
              <option value="meeting">Meetings</option>
              <option value="settings">Settings</option>
              <option value="variation">Variations</option>
              <option value="invoice">Invoices</option>
            </Select>
            <Select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} data-testid="filter-action">
              <option value="">All Actions</option>
              <option value="created">Created</option>
              <option value="updated">Updated</option>
              <option value="deleted">Deleted</option>
              <option value="status_changed">Status Changed</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </Select>
          </div>

          {logs.length === 0 ? (
            <Card className="p-8 text-center text-[#666]">No activity logs yet</Card>
          ) : (
            <Card className="divide-y divide-[#F0F0F0]" data-testid="activity-log-list">
              {logs.map(l => (
                <div key={l.id} className="flex items-start gap-3 p-3 hover:bg-[#FAFAFA]" data-testid={`audit-row-${l.id}`}>
                  <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <ClockCounterClockwise className="w-4 h-4 text-[#002FA7]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{l.user_name}</span>
                      <Badge variant={ACTION_COLORS[l.action] || 'default'}>{l.action}</Badge>
                      <span className="text-[10px] bg-[#E5E5E5] px-1.5 py-0.5 rounded">{l.entity_type}</span>
                    </div>
                    <p className="text-xs text-[#666] mt-0.5">{l.details}</p>
                  </div>
                  <span className="text-[10px] text-[#999] shrink-0">{new Date(l.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </Card>
          )}
        </>
      )}

      {activeTab === 'logins' && (
        loginLogs.length === 0 ? (
          <Card className="p-8 text-center text-[#666]">No login history</Card>
        ) : (
          <Card className="divide-y divide-[#F0F0F0]" data-testid="login-log-list">
            {loginLogs.map(l => (
              <div key={l.id} className="flex items-center gap-3 p-3 hover:bg-[#FAFAFA]" data-testid={`login-row-${l.id}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${l.action === 'LOGIN' ? 'bg-[#00C853]/10' : 'bg-[#E5E5E5]'}`}>
                  {l.action === 'LOGIN' ? <SignIn className="w-4 h-4 text-[#00C853]" /> : <SignOut className="w-4 h-4 text-[#666]" />}
                </div>
                <div className="flex-1">
                  <span className="text-sm font-medium">{l.user_name || l.user_email || l.user_id}</span>
                  <Badge variant={ACTION_COLORS[l.action]} className="ml-2">{l.action}</Badge>
                </div>
                <div className="text-right text-xs text-[#999]">
                  <p>{new Date(l.timestamp).toLocaleString()}</p>
                  <p>{l.ip_address}</p>
                </div>
              </div>
            ))}
          </Card>
        )
      )}
    </div>
  );
}
